import { createFileRoute } from '@tanstack/react-router';
import { Skeleton } from '@/components/ui/skeleton';
import { RowLink, RowGroup } from '@/components/ui/row-link';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getWebApp, getTgUser } from '@/lib/telegram';
import { api } from '@/lib/api';
import { Globe, LifeBuoy } from 'lucide-react';
import { APP_VERSION } from '@/lib/version';
import { AccountSwitcherCard } from '@/components/AccountSwitcherCard';

export const Route = createFileRoute('/profile')({ component: Profile });

const FALLBACK_FRANCHISE = import.meta.env.VITE_FRANCHISE_BOT_USERNAME || '';

function Profile() {
  const { t, i18n } = useTranslation();
  const tgUser = getTgUser();
  const { data: partner, isPending } = useQuery({ queryKey: ['partner'], queryFn: () => api.me() });
  const isDelegate = partner?.isDelegate === true;

  useEffect(() => {
    const tg = getWebApp();
    tg?.BackButton.hide();
    tg?.MainButton.hide();
  }, []);

  const displayName = tgUser
    ? [tgUser.first_name, tgUser.last_name].filter(Boolean).join(' ')
    : partner?.firstName || t('Tg user fallback');
  const username = tgUser?.username ?? partner?.username ?? null;
  const photoUrl = tgUser?.photo_url ?? null;
  const initials = (displayName.split(' ').map((p) => p[0]).join('') || '?').slice(0, 2).toUpperCase();
  const franchiseHandle = partner?.franchiseBotUsername || FALLBACK_FRANCHISE;

  const toggleLang = () => i18n.changeLanguage(i18n.language === 'en' ? 'ru' : 'en');
  const openSupport = () => {
    // Прямой контакт с человеком из саппорта франшизы.
    getWebApp()?.openTelegramLink('https://t.me/hex_support');
  };

  if (isPending && !partner) return <ProfileSkeleton />;

  return (
    <div className="p-4 pb-28 space-y-3">
      {/* Шапка профиля */}
      <div className="surface-card-raised rounded-[26px] p-5 relative overflow-hidden">
        <div className="relative flex items-center gap-4">
          <div className="relative shrink-0">
            {photoUrl ? (
              <img src={photoUrl} alt="" className="w-[66px] h-[66px] rounded-[22px] object-cover" />
            ) : (
              <div className="w-[66px] h-[66px] rounded-[22px] bg-white/[0.08] text-foreground font-display font-semibold text-[28px] flex items-center justify-center">
                {initials}
              </div>
            )}
            <span className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-money border-[3px] border-[#15161a]" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display font-semibold text-[19px] tracking-tight truncate">{displayName}</div>
            {username && <div className="font-mono text-[12.5px] text-dim truncate mt-0.5">@{username}</div>}
            <div className="flex items-center gap-2 mt-2.5">
              <span className="text-[12.5px] text-dim">
                {isDelegate ? 'Администратор кабинета' : 'Партнёр'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Деньги: баланс (зелёный) + всего заработано. Уже 360px по одной в ряд:
          семизначная сумма в половину экрана не помещается. */}
      <div className="grid grid-cols-1 min-[360px]:grid-cols-2 gap-2.5">
        <div className="surface-card rounded-[20px] p-4">
          <div className="text-[12.5px] text-dim">{t('Finance balance')}</div>
          <div className={`font-display font-medium nums mt-1.5 whitespace-nowrap ${amountSize(partner?.balanceRub ?? 0)}`}>
            {(partner?.balanceRub ?? 0).toLocaleString('ru')}{'\u00a0'}<span className="text-muted-foreground text-lg">₽</span>
          </div>
        </div>
        <div className="surface-card rounded-[20px] p-4">
          <div className="text-[12.5px] text-dim">Всего заработано</div>
          <div className={`font-display font-medium nums mt-1.5 whitespace-nowrap ${amountSize(partner?.totalEarnedRub ?? 0)}`}>
            {(partner?.totalEarnedRub ?? 0).toLocaleString('ru')}{'\u00a0'}<span className="text-muted-foreground text-lg">₽</span>
          </div>
        </div>
      </div>

      <AccountSwitcherCard />

      {/* Настройки */}
      <RowGroup>
        <RowLink
          accent="primary"
          icon={<Globe />}
          label={t('Language')}
          onClick={toggleLang}
          chevron={false}
          trailing={<span className="font-mono text-[13px] font-semibold text-primary-light uppercase">{i18n.language}</span>}
        />
        <RowLink accent="primary" icon={<LifeBuoy />} label={t('Support')} onClick={openSupport} />
      </RowGroup>

      {/* Футер: версия + tg id + франш-бот */}
      <div className="text-center pt-5 space-y-1.5">
        <div className="font-mono text-[10.5px] text-[#5a5c63] tracking-[0.5px]">
          v{APP_VERSION} · tg id {tgUser?.id ?? partner?.tgId ?? 0}
        </div>
        {franchiseHandle && <div className="font-mono text-[11px] text-primary-light">@{franchiseHandle}</div>}
      </div>

    </div>
  );
}

function ProfileSkeleton() {
  return (
    <div className="p-4 pb-28 space-y-3">
      <div className="surface-card rounded-[26px] p-5 flex items-center gap-4">
        <Skeleton className="w-[66px] h-[66px] rounded-[22px] shrink-0" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-5 w-32 rounded-md" />
          <Skeleton className="h-3.5 w-20 rounded-md" />
          <Skeleton className="h-4 w-16 rounded-full mt-1" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2.5">
        {[1, 2].map((i) => (
          <div key={i} className="surface-card rounded-[20px] p-4 space-y-2">
            <Skeleton className="h-3 w-20 rounded-md" />
            <Skeleton className="h-6 w-24 rounded-md" />
          </div>
        ))}
      </div>

      <div className="surface-card rounded-[20px] divide-y divide-white/[0.05] overflow-hidden">
        {[1, 2, 3, 4].map((i) => (
          <SkeletonRow key={i} />
        ))}
      </div>

      <div className="surface-card rounded-[20px] divide-y divide-white/[0.05] overflow-hidden">
        {[1, 2, 3].map((i) => (
          <SkeletonRow key={i} />
        ))}
      </div>

      <div className="flex justify-center pt-3">
        <Skeleton className="h-3 w-40 rounded-md" />
      </div>
    </div>
  );
}

function SkeletonRow() {
  return (
    <div className="p-4 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <Skeleton className="w-9 h-9 rounded-[11px] shrink-0" />
        <Skeleton className="h-4 w-28 rounded-md" />
      </div>
      <Skeleton className="w-4 h-4 rounded-md" />
    </div>
  );
}

/** Шрифт суммы в плитке профиля: от миллиона мельче, иначе «1 285 000 ₽» и «92 499 999 ₽»
 *  не помещались в половину экрана и уходили за край. */
function amountSize(rub: number): string {
  const abs = Math.abs(rub);
  if (abs >= 10_000_000) return 'text-[18px]';
  if (abs >= 1_000_000) return 'text-[21px]';
  return 'text-[26px]';
}
